import { FunctionComponent } from 'react';
import { Box, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  ResponsiveChartContainer,
  ChartsXAxis,
  ChartsYAxis,
  ChartsGrid,
  ChartsTooltip,
  ChartsReferenceLine,
} from '@mui/x-charts';
import { BarPlot } from '@mui/x-charts/BarChart';
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import gaiaTheme from '../../../../root-components/theme';

const fmtTco2e = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Pareto chart: descending contribution bars (left axis, tCO₂e) overlaid with a
 * cumulative-% line (right axis, 0–100) and an 80% reference line — shows how few
 * contributors drive the bulk of the footprint.
 */
export const GraphParetoChart: FunctionComponent<{
  title: string;
  tooltip?: string;
  items: Array<{ label: string; value: number; cumulativePct: number }>;
  chartHeight?: number;
  paperHeight?: number | string;
  onSelect?: (dataIndex: number) => void;
  /** Draw the bars and line in their final state at once (for the PDF render). */
  skipAnimation?: boolean;
}> = ({ title, tooltip, items, chartHeight = 420, paperHeight = '100%', onSelect, skipAnimation }) => {
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const theme = useTheme();

  const labels = items.map((item) => item.label);
  const values = items.map((item) => item.value);
  const cumulative = items.map((item) => item.cumulativePct);

  return (
    <Paper variant='outlined' sx={{ p: 4, height: paperHeight, display: 'flex', flexDirection: 'column' }}>
      <Stack direction='row' alignItems='center'>
        <Typography variant='subtitle1' sx={{ flex: 1 }}>
          <b>{title}</b>
        </Typography>
        {tooltip && !isMobileMode && <TooltipButton text={tooltip} noPadding={true} placement='right' />}
      </Stack>
      {items.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight }}>
          <Typography color='text.secondary'>No data available.</Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: chartHeight }}>
          <ResponsiveChartContainer
            height={chartHeight}
            series={[
              {
                type: 'bar',
                id: 'contribution',
                yAxisKey: 'bars',
                label: 'Contribution (tCO₂e)',
                color: theme.palette.primary.main,
                data: values,
                valueFormatter: (value: number | null) => (value == null ? '' : `${fmtTco2e.format(value)} tCO₂e`),
              },
              {
                type: 'line',
                id: 'cumulative',
                yAxisKey: 'pct',
                label: 'Cumulative %',
                color: theme.palette.warning.main,
                curve: 'linear',
                data: cumulative,
                valueFormatter: (value: number | null) => (value == null ? '' : `${value.toFixed(1)}%`),
              },
            ]}
            xAxis={[{
              id: 'cats',
              data: labels,
              scaleType: 'band',
              tickLabelStyle: { fontSize: 11, angle: -30, textAnchor: 'end' },
            }]}
            yAxis={[
              { id: 'bars', scaleType: 'linear' },
              { id: 'pct', scaleType: 'linear', min: 0, max: 100, position: 'right' },
            ]}
            margin={{ left: 64, right: 60, top: 16, bottom: 96 }}
          >
            <ChartsGrid horizontal />
            <BarPlot
              borderRadius={5}
              skipAnimation={skipAnimation}
              onItemClick={onSelect ? ((_event: any, bar: { dataIndex: number }) => onSelect(bar.dataIndex)) : undefined}
              sx={onSelect ? { cursor: 'pointer' } : undefined}
            />
            <LinePlot skipAnimation={skipAnimation} />
            <MarkPlot skipAnimation={skipAnimation} />
            <ChartsReferenceLine
              axisId='pct'
              y={80}
              lineStyle={{ strokeDasharray: '5 5', stroke: theme.palette.text.secondary }}
              label='80%'
              labelAlign='end'
            />
            <ChartsXAxis axisId='cats' />
            <ChartsYAxis axisId='bars' label='tCO₂e' />
            <ChartsYAxis axisId='pct' position='right' label='Cumulative %' />
            <ChartsTooltip trigger='axis' />
          </ResponsiveChartContainer>
        </Box>
      )}
    </Paper>
  );
};
